import useI18n from "@i18n";
import { Alert, Snackbar } from "@mui/material";
import React, { createContext, ReactNode, useCallback, useContext, useState } from "react";

type NotificationType = "success" | "error" | "info" | "warning";

interface NotificationDialogConfig {
  type: NotificationType;
  title: string;
  message: string;
  additionalContent?: ReactNode;
  customIcon?: ReactNode;
  hasCustomAction?: boolean;
  onClose?: () => void;
}

interface NotificationDialogContextType {
  showNotification: (config: NotificationDialogConfig) => void;
  hideNotification: (callback?: () => void) => void;
  showNotificationToast: (i18ni18nKey: string, type: NotificationType) => void;
  isVisible: boolean;
  currentNotification: NotificationDialogConfig | null;
}

const NotificationDialogContext = createContext<NotificationDialogContextType | undefined>(undefined);

export const NotificationDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<NotificationDialogConfig | null>(null);
  const [toast, setToast] = useState<{ message: string; type: NotificationType } | null>(null);
  const showNotificationToast = useCallback(
    (i18nKey: string, type: NotificationType) => setToast({ message: i18n(i18nKey), type }),
    [i18n, setToast]
  );

  const showNotification = useCallback((config: NotificationDialogConfig) => {
    setCurrentNotification(config);
    setIsVisible(true);
  }, []);

  const hideNotification = useCallback(
    (callback?: () => void) => {
      if (currentNotification?.onClose) {
        currentNotification.onClose();
      }
      setIsVisible(false);
      // Clear the notification config after animation would complete
      setTimeout(() => setCurrentNotification(null), 300);
      callback?.();
    },
    [currentNotification]
  );

  return (
    <NotificationDialogContext.Provider
      value={{
        showNotification,
        hideNotification,
        showNotificationToast,
        isVisible,
        currentNotification,
      }}
    >
      {children}
      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={(_, reason) => reason !== "clickaway" && setToast(null)}>
        {toast ? (
          <Alert severity={toast.type} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationDialogContext.Provider>
  );
};

export const useOptionalNotificationDialog = () => useContext(NotificationDialogContext);

export const useNotificationDialog = () => {
  const context = useOptionalNotificationDialog();
  if (context === undefined) {
    throw new Error("useNotificationDialog must be used within a NotificationDialogProvider");
  }
  return context;
};
